// Team API 서비스
//
// 요청은 공용 axios 클라이언트(apiService.client)를 사용한다.
// 응답은 서버 응답 봉투({ success, data, message })를 그대로 돌려주며,
// HTTP 오류는 ApiServiceError 로 throw 되므로 호출부에서 try/catch 로 처리한다.

import { apiService } from './api'

export type TeamRole = 'editor' | 'viewer'

// 팀 라우트는 data 봉투 없이 최상위 키로 응답한다 (예: { success, teams })
interface ApiEnvelope {
  success: boolean
  message?: string
  teams?: Array<Record<string, unknown>>
  team?: Record<string, unknown> & { name?: string }
  invitations?: Array<Record<string, unknown>>
  [key: string]: unknown
}

export class TeamService {
  // 팀 목록 조회
  static async getTeams() {
    const response = await apiService.client.get<ApiEnvelope>('/teams')
    return response.data
  }

  // 새 팀 생성
  static async createTeam(data: { name: string; description?: string }) {
    const response = await apiService.client.post<ApiEnvelope>('/teams', data)
    return response.data
  }

  // 팀원 초대
  static async inviteTeamMember(
    teamId: string,
    data: { email: string; role: TeamRole }
  ) {
    const response = await apiService.client.post<ApiEnvelope>(
      `/teams/${teamId}/invite`,
      data
    )
    return response.data
  }

  // 팀원 제거
  static async removeTeamMember(teamId: string, memberId: string) {
    const response = await apiService.client.delete<ApiEnvelope>(
      `/teams/${teamId}/members/${memberId}`
    )
    return response.data
  }

  // 팀원 역할 변경
  static async changeTeamMemberRole(
    teamId: string,
    memberId: string,
    role: TeamRole
  ) {
    const response = await apiService.client.put<ApiEnvelope>(
      `/teams/${teamId}/members/${memberId}/role`,
      { role }
    )
    return response.data
  }

  // 팀 정보 수정
  static async updateTeam(
    teamId: string,
    data: { name?: string; description?: string }
  ) {
    const response = await apiService.client.put<ApiEnvelope>(
      `/teams/${teamId}`,
      data
    )
    return response.data
  }

  // 팀 삭제
  static async deleteTeam(teamId: string) {
    const response = await apiService.client.delete<ApiEnvelope>(
      `/teams/${teamId}`
    )
    return response.data
  }

  // 팀 초대 수락
  static async joinTeam(token: string) {
    const response = await apiService.client.post<ApiEnvelope>(
      `/teams/join/${token}`
    )
    return response.data
  }

  // 팀 초대 목록
  static async getInvitations(teamId: string) {
    const response = await apiService.client.get<ApiEnvelope>(
      `/teams/${teamId}/invitations`
    )
    return response.data
  }

  // 초대 재발송
  static async resendInvitation(teamId: string, invitationId: string) {
    const response = await apiService.client.post<ApiEnvelope>(
      `/teams/${teamId}/invitations/${invitationId}/resend`
    )
    return response.data
  }

  // 초대 취소
  static async cancelInvitation(teamId: string, invitationId: string) {
    const response = await apiService.client.delete<ApiEnvelope>(
      `/teams/${teamId}/invitations/${invitationId}`
    )
    return response.data
  }
}

export default TeamService
